<template>
  <div>
  <ai-tab-nav />
  <div class="ai-llm-page">
    <page-header title="AI 大模型配置">
      <template #actions>
        <el-button size="small" icon="el-icon-refresh" @click="loadList">刷新</el-button>
        <el-button size="small" icon="el-icon-plus" type="success" @click="openDialog(null)">新增配置</el-button>
      </template>
    </page-header>

    <el-alert
      title="说明"
      type="info"
      :closable="false"
      style="margin-bottom:12px">
      <div slot="default">
        修改配置后立即生效,无需重启。API Key 在列表里脱敏显示(只显示前4后4位)。
        编辑时若 Key 留空,后端会保留原值,不会被清空。
      </div>
    </el-alert>

    <el-table :data="list" v-loading="loading" stripe border size="small" style="width:100%">
      <el-table-column prop="configCode" label="配置编码" width="160" />
      <el-table-column prop="configName" label="展示名称" width="160" />
      <el-table-column prop="providerCode" label="提供商" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="providerTagType(row.providerCode)">{{ providerLabel(row.providerCode) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="model" label="模型 ID" min-width="180" show-overflow-tooltip />
      <el-table-column label="视觉" width="70" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="row.supportsVision === 1" type="success" size="mini">看图</el-tag>
          <span v-else style="color:#bbb">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="apiKey" label="API Key (脱敏)" width="160">
        <template slot-scope="{ row }">
          <code class="masked-key">{{ row.apiKey }}</code>
        </template>
      </el-table-column>
      <el-table-column prop="temperature" label="温度" width="70" align="center" />
      <el-table-column prop="maxTokens" label="max_tokens" width="100" align="center" />
      <el-table-column prop="isDefault" label="默认" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="row.isDefault === 1" type="success" size="mini">默认</el-tag>
          <el-button v-else type="text" size="mini" @click="handleSetDefault(row)">设为默认</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="enabled" label="启用" width="70" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.enabled === 1 ? 'success' : 'info'">
            {{ row.enabled === 1 ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="testConnection(row)" :loading="testingId === row.id">测试</el-button>
          <el-button type="text" size="small" @click="openDialog(row)">编辑</el-button>
          <el-button type="text" size="small" style="color:#F56C6C" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editForm.id ? '编辑大模型配置' : '新增大模型配置'" :visible.sync="dialogVisible" width="640px" append-to-body>
      <el-form :model="editForm" label-width="120px" :rules="rules" ref="editFormRef" size="small">
        <el-form-item label="配置编码" prop="configCode">
          <el-input v-model="editForm.configCode" placeholder="如 volc-doubao-pro" :disabled="!!editForm.id" />
        </el-form-item>
        <el-form-item label="展示名称" prop="configName">
          <el-input v-model="editForm.configName" placeholder="如 火山豆包 Pro" />
        </el-form-item>
        <el-form-item label="提供商" prop="providerCode">
          <el-select v-model="editForm.providerCode" style="width:100%" placeholder="对应 Java LlmClient 的 providerCode()">
            <el-option label="火山方舟 (volc)" value="volc" />
            <el-option label="阿里通义 (qwen)" value="qwen" />
            <el-option label="DeepSeek (deepseek)" value="deepseek" />
            <el-option label="OpenAI 兼容 (openai)" value="openai" />
          </el-select>
        </el-form-item>
        <el-form-item label="API URL" prop="apiUrl">
          <el-input v-model="editForm.apiUrl" placeholder="完整 chat/completions 地址" />
        </el-form-item>
        <el-form-item label="API Key" prop="apiKey">
          <el-input v-model="editForm.apiKey" type="password" show-password
            :placeholder="editForm.id ? '留空表示保留原值' : '请输入真实 API Key'" />
        </el-form-item>
        <el-form-item label="模型 ID" prop="model">
          <el-input v-model="editForm.model" placeholder="如 doubao-1-5-pro-32k-250115" />
        </el-form-item>
        <el-form-item label="支持视觉输入">
          <el-switch v-model="supportsVisionValue" />
          <span style="margin-left:8px;color:#999;font-size:12px">
            开启后在需要识别图片时会自动路由到本配置。豆包 Vision、GPT-4V 、Claude 3.5 等需要勾选。
          </span>
        </el-form-item>
        <el-form-item label="温度">
          <el-input-number v-model="editForm.temperature" :step="0.1" :min="0" :max="2" :precision="2" />
          <span style="margin-left:8px;color:#999;font-size:12px">建议 0~0.3,审核场景越低越稳定</span>
        </el-form-item>
        <el-form-item label="max_tokens">
          <el-input-number v-model="editForm.maxTokens" :step="256" :min="256" :max="32000" />
        </el-form-item>
        <el-form-item label="超时(毫秒)">
          <el-input-number v-model="editForm.timeoutMs" :step="5000" :min="5000" :max="300000" />
        </el-form-item>
        <el-form-item label="重试次数">
          <el-input-number v-model="editForm.retryMaxAttempts" :step="1" :min="0" :max="5" />
          <span style="margin-left:8px;color:#999;font-size:12px">不含首次调用,0=不重试</span>
        </el-form-item>
        <el-form-item label="重试退避基数(ms)">
          <el-input-number v-model="editForm.retryBaseDelayMs" :step="500" :min="500" :max="30000" />
          <span style="margin-left:8px;color:#999;font-size:12px">指数退避: base * 2^n</span>
        </el-form-item>
        <el-form-item label="额外参数 JSON">
          <el-input v-model="editForm.extraParams" type="textarea" :rows="2" placeholder='{"top_p": 0.9}' />
        </el-form-item>
        <el-form-item label="设为默认">
          <el-switch v-model="defaultValue" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="enabledValue" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="editForm.remark" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </div>
    </el-dialog>

    <!-- 测试结果弹窗 -->
    <el-dialog title="连通性测试" :visible.sync="testVisible" width="520px" append-to-body>
      <div v-if="testResult">
        <el-result :icon="testResult.ok ? 'success' : 'error'"
          :title="testResult.ok ? '连接正常' : '连接失败'"
          :sub-title="testResult.ok ? '' : (testResult.error || '')">
        </el-result>
        <el-descriptions v-if="testResult.ok" :column="1" border size="small">
          <el-descriptions-item label="返回内容">{{ testResult.content }}</el-descriptions-item>
          <el-descriptions-item label="耗时">{{ testResult.costMs }} ms</el-descriptions-item>
          <el-descriptions-item label="输入 tokens">{{ testResult.promptTokens }}</el-descriptions-item>
          <el-descriptions-item label="输出 tokens">{{ testResult.completionTokens }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer">
        <el-button size="small" @click="testVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
  </div>
</template>

<script>
import { aiLlmApi } from './api.js';
import AiTabNav from './AiTabNav.vue';
import PageHeader from '@/components/PageHeader.vue';

const PROVIDER_LABEL = { volc: '火山方舟', qwen: '通义', deepseek: 'DeepSeek', openai: 'OpenAI' };
const PROVIDER_TAG = { volc: 'danger', qwen: 'warning', deepseek: 'success', openai: '' };

export default {
  name: 'AiLlmManage',
  components: { AiTabNav, PageHeader },
  data() {
    return {
      list: [],
      loading: false,
      saving: false,
      testingId: null,
      dialogVisible: false,
      editForm: this.emptyForm(),
      rules: {
        configCode: [{ required: true, message: '编码必填', trigger: 'blur' }],
        configName: [{ required: true, message: '名称必填', trigger: 'blur' }],
        providerCode: [{ required: true, message: '提供商必选', trigger: 'change' }],
        apiUrl: [{ required: true, message: 'API URL 必填', trigger: 'blur' }],
        model: [{ required: true, message: '模型 ID 必填', trigger: 'blur' }]
      },
      testVisible: false,
      testResult: null
    };
  },
  computed: {
    defaultValue: {
      get() { return this.editForm.isDefault === 1; },
      set(v) { this.editForm.isDefault = v ? 1 : 0; }
    },
    enabledValue: {
      get() { return this.editForm.enabled === 1; },
      set(v) { this.editForm.enabled = v ? 1 : 0; }
    },
    supportsVisionValue: {
      get() { return this.editForm.supportsVision === 1; },
      set(v) { this.editForm.supportsVision = v ? 1 : 0; }
    }
  },
  created() {
    this.loadList();
  },
  methods: {
    providerLabel(p) { return PROVIDER_LABEL[p] || p; },
    providerTagType(p) { return PROVIDER_TAG[p] || ''; },

    emptyForm() {
      return {
        id: null, configCode: '', configName: '', providerCode: 'volc',
        apiUrl: 'https://ark.cn-beijing.volces.com/api/v3/chat/completions',
        apiKey: '', model: '', temperature: 0.1, maxTokens: 4096, timeoutMs: 60000,
        retryMaxAttempts: 3, retryBaseDelayMs: 2000,
        supportsVision: 0,
        extraParams: '', isDefault: 0, enabled: 1, remark: ''
      };
    },

    async loadList() {
      this.loading = true;
      try {
        const resp = await aiLlmApi.list();
        if (resp.code === 200) {
          this.list = resp.data || [];
        } else {
          this.$message.error(resp.msg || '加载失败');
        }
      } catch (e) {
        this.$message.error('网络错误');
      } finally {
        this.loading = false;
      }
    },

    openDialog(row) {
      if (row) {
        this.editForm = { ...row, apiKey: '' };  // 编辑时清空 key 输入框
      } else {
        this.editForm = this.emptyForm();
      }
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.editFormRef && this.$refs.editFormRef.clearValidate());
    },

    handleSave() {
      this.$refs.editFormRef.validate(async valid => {
        if (!valid) return;
        // 新增时 apiKey 必填
        if (!this.editForm.id && !this.editForm.apiKey) {
          this.$message.error('新增配置时 API Key 必填');
          return;
        }
        this.saving = true;
        try {
          const fn = this.editForm.id
            ? aiLlmApi.update(this.editForm.id, this.editForm)
            : aiLlmApi.create(this.editForm);
          const resp = await fn;
          if (resp.code === 200) {
            this.$message.success('保存成功');
            this.dialogVisible = false;
            this.loadList();
          } else {
            this.$message.error(resp.msg || '保存失败');
          }
        } catch (e) {
          this.$message.error('网络错误');
        } finally {
          this.saving = false;
        }
      });
    },

    handleDelete(row) {
      this.$confirm(`确认删除配置「${row.configName}」?`, '提示', { type: 'warning' })
        .then(async () => {
          const resp = await aiLlmApi.remove(row.id);
          if (resp.code === 200) {
            this.$message.success('已删除');
            this.loadList();
          } else {
            this.$message.error(resp.msg || '删除失败');
          }
        }).catch(() => {});
    },

    async handleSetDefault(row) {
      const resp = await aiLlmApi.setDefault(row.id);
      if (resp.code === 200) {
        this.$message.success('已设为默认');
        this.loadList();
      } else {
        this.$message.error(resp.msg || '操作失败');
      }
    },

    async testConnection(row) {
      this.testingId = row.id;
      try {
        const resp = await aiLlmApi.test(row.id);
        if (resp.code === 200) {
          this.testResult = resp.data;
          this.testVisible = true;
        } else {
          this.$message.error(resp.msg || '测试失败');
        }
      } catch (e) {
        this.$message.error('网络错误');
      } finally {
        this.testingId = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-llm-page {
  padding: 20px;
  background: #fff;
  min-height: 100%;

  /* .page-header 已抽到 PageHeader 组件 */

  .masked-key {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    color: #666;
  }
}
</style>
